import { useEffect, useRef, useState } from 'react';
import { base44 } from '@/api/base44Client';

let googleMapsLoaded = false;
let loadingPromise = null;

async function loadGoogleMaps() {
  if (googleMapsLoaded) return;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const res = await base44.functions.invoke('getGoogleMapsKey', {});
    const apiKey = res.data?.apiKey;
    if (!apiKey) return;

    await new Promise((resolve, reject) => {
      if (window.google?.maps?.places) { googleMapsLoaded = true; resolve(); return; }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.onload = () => { googleMapsLoaded = true; resolve(); };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  })();

  return loadingPromise;
}

/**
 * Drop-in replacement for a plain <input> with Google Places Autocomplete.
 * Props: value, onChange (receives full address string), name, placeholder, className, required
 */
export default function AddressAutocomplete({ value, onChange, name, placeholder, className, required }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    loadGoogleMaps().then(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!ready || !inputRef.current || autocompleteRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      fields: ['formatted_address'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const address = place.formatted_address || inputRef.current.value;
      onChange({ target: { name: name || 'address', value: address } });
    });

    autocompleteRef.current = autocomplete;
  }, [ready]);

  return (
    <input
      ref={inputRef}
      type="text"
      name={name}
      defaultValue={value}
      placeholder={placeholder}
      required={required}
      className={className}
      onChange={onChange}
    />
  );
}